import type { Href } from "expo-router";
import { router } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, Text, useWindowDimensions, View } from "react-native";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { Path } from "react-native-svg";

import { BrandBackdrop } from "../../../components/student/BrandBackdrop";
import { StudentPortalShell } from "../../../components/student/StudentPortalShell";
import { Avatar, EmptyState, Skeleton } from "../../../components/ui";
import { useAuth } from "../../../lib/authContext";
import { getCachedTeams, listMyTeams, type StudentTeamRow } from "../../../lib/studentTeams";
import { useStudentPortalShellData } from "../../../lib/useStudentPortalShellData";


/**
 * v2 student "My Teams" page. Replaces the retired redirect with the
 * design-aligned layout from the handoff package: kicker counts, serif
 * "Your *teams*." headline, then a responsive grid of team cards. Each
 * card surfaces a quick way into the team workspace (Open chat) and the
 * program's team-tab (Manage).
 */
export default function StudentTeamsScreen() {
  const { user } = useAuth();
  const shell = useStudentPortalShellData();
  const { width } = useWindowDimensions();
  const wide = width >= 900;

  const [active, setActive] = useState<StudentTeamRow[]>([]);
  const [past, setPast] = useState<StudentTeamRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setActive([]);
      setPast([]);
      setLoading(false);
      return;
    }
    const { active: a, past: p } = await listMyTeams(user.id);
    setActive(a);
    setPast(p);
    setLoading(false);
  }, [user]);

  // Stale-while-revalidate: on mount, paint the last-known list from the
  // in-memory cache so the page feels instant when bouncing between tabs.
  // Always kick off a background refetch so any change since the cache
  // was written lands without a manual reload.
  useEffect(() => {
    if (!user) return;
    const cached = getCachedTeams(user.id);
    if (cached) {
      setActive(cached.active);
      setPast(cached.past);
      setLoading(false);
    }
    void refresh();
  }, [user, refresh]);

  const captaincies = useMemo(
    () => active.filter((t) => t.member_role === "captain").length,
    [active]
  );

  return (
    <StudentPortalShell
      topbarTitle="Teams"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 80 }}
      >
        <BrandBackdrop tone="sand" />

        <Hero
          teamCount={active.length + past.length}
          captaincies={captaincies}
          // Skeleton kicker on cold load only — once cached or fetched
          // data lands, render the real counts immediately.
          loading={loading && active.length === 0 && past.length === 0}
          onCreate={() => router.push("/student/find" as Href)}
        />

        <View className="max-w-[1280px] px-5 pt-4 md:px-11">
          {loading ? (
            <SkeletonGrid wide={wide} />
          ) : active.length === 0 && past.length === 0 ? (
            <View
              className="overflow-hidden rounded-lg bg-white"
              style={{ borderWidth: 1, borderColor: STUDENT_THEME.border }}
            >
              <View className="px-6 py-12">
                <EmptyState
                  title="No teams yet"
                  subtitle="Find a competition and join a public team, accept an invite, or create your own."
                />
              </View>
            </View>
          ) : (
            <>
              {active.length > 0 ? (
                <Section title="Active teams" count={active.length}>
                  <TeamGrid teams={active} wide={wide} />
                </Section>
              ) : null}
              {past.length > 0 ? (
                <View style={{ marginTop: active.length > 0 ? 32 : 0 }}>
                  <Section title="Past teams" count={past.length}>
                    <TeamGrid teams={past} wide={wide} dim />
                  </Section>
                </View>
              ) : null}
            </>
          )}
        </View>
      </ScrollView>
    </StudentPortalShell>
  );
}

function Hero({
  teamCount,
  captaincies,
  loading,
  onCreate,
}: {
  teamCount: number;
  captaincies: number;
  loading: boolean;
  onCreate: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);

  const kicker = loading
    ? "Loading…"
    : (() => {
        const teams = teamCount === 1 ? "1 team" : `${teamCount} teams`;
        const caps =
          captaincies === 0
            ? "no captaincies"
            : captaincies === 1
            ? "1 captaincy"
            : `${captaincies} captaincies`;
        return `${teams} · ${caps}`;
      })();

  return (
    <View
      style={{
        paddingHorizontal: 24,
        paddingTop: 28,
        paddingBottom: 24,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.SAND_DARK.hex,
        position: "relative",
      }}
      className="md:px-11"
    >
      <View
        pointerEvents="none"
        style={[
          { position: "absolute", left: 24, right: 24, bottom: -1, height: 1 },
          isWeb
            ? ({
                backgroundImage:
                  "linear-gradient(90deg, transparent, rgba(181,135,58,0.45), transparent)",
              } as object)
            : { backgroundColor: STUDENT_THEME.accentBorderSoft },
        ]}
      />

      <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <View style={{ height: 1, width: 28, backgroundColor: COLORS.AMBER.hex }} />
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: COLORS.AMBER.hex,
          }}
        >
          {kicker}
        </Text>
      </View>

      <Text
        style={{
          fontFamily: FONTS.display,
          fontSize: 44,
          lineHeight: 46,
          letterSpacing: -0.5,
          color: COLORS.FOREST.hex,
          marginBottom: 8,
        }}
      >
        Your{" "}
        <Text
          style={{
            fontFamily: FONTS.display,
            fontStyle: "italic",
            color: COLORS.AMBER.hex,
          }}
        >
          teams
        </Text>
        .
      </Text>
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13.5,
          lineHeight: 21,
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: 16,
          maxWidth: 580,
        }}
      >
        Teams are how you and your school enter competitions together. Captains manage the roster
        and coordinate the team.
      </Text>

      <Pressable
        onPress={onCreate}
        accessibilityRole="button"
        accessibilityLabel="Create or join a team"
        {...(isWeb
          ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
          : {})}
        style={[
          {
            alignSelf: "flex-start",
            backgroundColor: hover ? COLORS.AMBER_WARM.hex : COLORS.AMBER.hex,
            borderRadius: 5,
            paddingVertical: 12,
            paddingHorizontal: 20,
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          },
          isWeb
            ? ({
                cursor: "pointer",
                transition: "background-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
                boxShadow: hover
                  ? "0 12px 24px -10px rgba(28,43,26,0.55)"
                  : "0 8px 20px -10px rgba(28,43,26,0.45)",
                transform: [{ translateY: hover ? -1 : 0 }],
              } as object)
            : {},
        ]}
      >
        <Svg width={13} height={13} viewBox="0 0 12 12" fill="none">
          <Path
            d="M6 2v8M2 6h8"
            stroke={COLORS.FOREST.hex}
            strokeWidth={2}
            strokeLinecap="round"
          />
        </Svg>
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.76,
            textTransform: "uppercase",
            color: COLORS.FOREST.hex,
          }}
        >
          Create or join a team
        </Text>
      </Pressable>
    </View>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <View>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 10.5,
            letterSpacing: 2.1,
            textTransform: "uppercase",
            color: STUDENT_THEME.textOnSurfaceDim,
          }}
        >
          {title}
        </Text>
        {count != null && count > 0 ? (
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 14,
              color: COLORS.AMBER.hex,
            }}
          >
            {count}
          </Text>
        ) : null}
      </View>
      {children}
    </View>
  );
}

function TeamGrid({
  teams,
  wide,
  dim,
}: {
  teams: StudentTeamRow[];
  wide: boolean;
  dim?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 16,
      }}
    >
      {teams.map((t) => (
        <View
          key={t.team_id}
          style={{
            flexBasis: 0,
            flexGrow: 1,
            flexShrink: 1,
            minWidth: wide ? 320 : 280,
            width: "100%" as unknown as number,
          }}
        >
          <TeamCard team={t} dim={!!dim} />
        </View>
      ))}
    </View>
  );
}

function TeamCard({ team, dim }: { team: StudentTeamRow; dim: boolean }) {
  const isWeb = Platform.OS === "web";
  const initial = (team.team_name?.trim()?.[0] ?? "?").toUpperCase();
  const isCaptain = team.member_role === "captain";

  return (
    <View
      style={[
        {
          backgroundColor: STUDENT_THEME.surfaceRaisedBg,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: STUDENT_THEME.border,
          paddingVertical: 22,
          paddingHorizontal: 24,
          opacity: dim ? 0.85 : 1,
        },
        isWeb
          ? ({
              boxShadow:
                "0 1px 2px rgba(28,43,26,0.04), 0 4px 14px rgba(28,43,26,0.05)",
            } as object)
          : null,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: 10,
            backgroundColor: COLORS.FOREST.hex,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.displaySemiBold,
              fontSize: 22,
              color: COLORS.AMBER.hex,
            }}
          >
            {initial}
          </Text>
        </View>
        <View
          style={{
            paddingHorizontal: 11,
            paddingVertical: 4,
            borderRadius: 999,
            borderWidth: 1,
            backgroundColor: isCaptain
              ? STUDENT_THEME.accentTintSoft
              : "rgba(28,43,26,0.06)",
            borderColor: isCaptain
              ? STUDENT_THEME.accentBorderSoft
              : "rgba(28,43,26,0.14)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: isCaptain ? COLORS.AMBER.hex : STUDENT_THEME.textOnSurfaceDim,
            }}
          >
            {isCaptain ? "Captain" : "Member"}
          </Text>
        </View>
      </View>

      <Text
        numberOfLines={1}
        style={{
          fontFamily: FONTS.displayMedium,
          fontSize: 22,
          lineHeight: 26,
          color: COLORS.FOREST.hex,
          marginBottom: 6,
        }}
      >
        {team.team_name}
      </Text>
      <Text
        numberOfLines={1}
        style={{
          fontFamily: FONTS.body,
          fontSize: 12.5,
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: 18,
        }}
      >
        {`${team.member_count} member${team.member_count === 1 ? "" : "s"}  ·  ${team.competition_name}`}
      </Text>

      <View style={{ flexDirection: "row", marginBottom: 18, alignItems: "center" }}>
        {team.members.slice(0, 5).map((m, i) => (
          <View
            key={m.student_id}
            style={{
              marginLeft: i === 0 ? 0 : -8,
              borderWidth: 2,
              borderColor: STUDENT_THEME.surfaceRaisedBg,
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <Avatar name={m.display_name} imageUrl={m.avatar_url} size="sm" />
          </View>
        ))}
        {team.members.length > 5 ? (
          <View
            style={{
              marginLeft: -8,
              width: 28,
              height: 28,
              borderRadius: 999,
              borderWidth: 2,
              borderColor: STUDENT_THEME.surfaceRaisedBg,
              backgroundColor: "rgba(28,43,26,0.06)",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 9.5,
                color: STUDENT_THEME.textOnSurfaceDim,
              }}
            >
              +{team.members.length - 5}
            </Text>
          </View>
        ) : null}
      </View>

      <View
        style={{
          flexDirection: "row",
          gap: 10,
          paddingTop: 14,
          borderTopWidth: 1,
          borderTopColor: COLORS.SAND_LINE.hex,
        }}
      >
        <FlatButton
          label="Open chat"
          variant="secondary"
          onPress={() =>
            router.push(`/student/competition/${team.competition_id}/team` as Href)
          }
        />
        <FlatButton
          label="Manage"
          variant="dark"
          onPress={() =>
            router.push(`/student/competition/${team.competition_id}?tab=team` as Href)
          }
        />
      </View>
    </View>
  );
}

function FlatButton({
  label,
  variant,
  onPress,
}: {
  label: string;
  variant: "secondary" | "dark";
  onPress: () => void;
}) {
  const isWeb = Platform.OS === "web";
  const [hover, setHover] = useState(false);
  const isDark = variant === "dark";

  const baseBg = isDark ? COLORS.FOREST.hex : "transparent";
  const hoverBg = isDark ? COLORS.FOREST_MID.hex : "rgba(28,43,26,0.04)";
  const baseColor = isDark ? COLORS.LINEN.hex : COLORS.FOREST.hex;
  const baseBorder = isDark ? COLORS.FOREST.hex : COLORS.SAND_DARK.hex;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      {...(isWeb
        ? ({ onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } as const)
        : {})}
      style={[
        {
          flex: 1,
          paddingVertical: 11,
          paddingHorizontal: 12,
          borderRadius: 5,
          borderWidth: 1,
          borderColor: baseBorder,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: hover && isWeb ? hoverBg : baseBg,
        },
        isWeb ? ({ cursor: "pointer", transition: "background-color 0.15s ease" } as object) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 1.68,
          textTransform: "uppercase",
          color: baseColor,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function SkeletonGrid({ wide }: { wide: boolean }) {
  return (
    <View>
      <Skeleton width={140} height={11} />
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 16, marginTop: 14 }}>
        {[0, 1].map((k) => (
          <View
            key={k}
            style={{
              flexBasis: 0,
              flexGrow: 1,
              flexShrink: 1,
              minWidth: wide ? 320 : 280,
              width: "100%" as unknown as number,
              backgroundColor: STUDENT_THEME.surfaceRaisedBg,
              borderWidth: 1,
              borderColor: STUDENT_THEME.border,
              borderRadius: 12,
              padding: 22,
            }}
          >
            <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
              <Skeleton width={48} height={48} radius={10} />
              <Skeleton width={72} height={20} radius={999} />
            </View>
            <Skeleton width={"60%" as unknown as number} height={22} style={{ marginTop: 14 }} />
            <Skeleton width={"80%" as unknown as number} height={11} style={{ marginTop: 8 }} />
            <View style={{ flexDirection: "row", marginTop: 18, gap: -8 }}>
              {[0, 1, 2, 3].map((j) => (
                <Skeleton key={j} width={28} height={28} radius={999} />
              ))}
            </View>
            <View style={{ flexDirection: "row", gap: 10, marginTop: 18 }}>
              <Skeleton width={"50%" as unknown as number} height={36} radius={5} />
              <Skeleton width={"50%" as unknown as number} height={36} radius={5} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}
